import { useState } from 'react';
import { Alert, Flex, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { PermissionKey } from '@evimo/types/enums/permission-key';
import { AuthGuard } from '~/app/auth/guard/AuthGuard';
import { useWorkOrderKanbanPage } from './hooks/useWorkOrderKanbanPage';
import { WorkOrderKanbanBoard } from './components/WorkOrderKanbanBoard/WorkOrderKanbanBoard';
import { WorkOrderKanbanFilters } from './components/WorkOrderKanbanFilters/WorkOrderKanbanFilters';
import { WorkOrderAssignDialog } from './components/WorkOrderAssignDialog/WorkOrderAssignDialog';
import { WorkOrderHoldDialog } from './components/WorkOrderHoldDialog/WorkOrderHoldDialog';
import { WorkOrderCompleteModal } from './components/WorkOrderCompleteModal/WorkOrderCompleteModal';
import { MoveTicketDialog } from './components/MoveTicketDialog/MoveTicketDialog';
import { FollowUpPanel } from './components/FollowUpPanel/FollowUpPanel';
import { WorkOrderDetailDrawer } from './components/WorkOrderDetailDrawer/WorkOrderDetailDrawer';
import { WorkOrderOptimizerAccordion } from './components/WorkOrderOptimizerAccordion/WorkOrderOptimizerAccordion';
import { WorkOrderOptimizerTeaser } from './components/WorkOrderOptimizerTeaser/WorkOrderOptimizerTeaser';
import { WORK_ORDER_UI } from '~/app/components/maintenance/work-orders/work-order-ui-colors';
import { OPTIMIZER_ACCORDION_SECTION_ID } from '~/app/components/maintenance/work-orders/optimizer/constants';

function WorkOrderKanbanPageView() {
  const { t } = useTranslation('maintenance');
  const page = useWorkOrderKanbanPage();
  const [optimizerOpen, setOptimizerOpen] = useState(false);

  const openOptimizerAccordion = () => {
    setOptimizerOpen(true);

    const scrollSuggestionsIntoView = () => {
      const section = document.getElementById(OPTIMIZER_ACCORDION_SECTION_ID);
      if (!section) return;

      // Landing shell scrolls `.landing-content-scroll`, not `window`.
      const scrollParent =
        section.closest('.landing-content-scroll') ??
        document.querySelector('.landing-content-scroll');

      if (scrollParent instanceof HTMLElement) {
        const parentRect = scrollParent.getBoundingClientRect();
        const sectionRect = section.getBoundingClientRect();
        const nextTop =
          scrollParent.scrollTop + (sectionRect.top - parentRect.top) - 12;
        scrollParent.scrollTo({
          top: Math.max(0, nextTop),
          behavior: 'smooth',
        });
        return;
      }

      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    // Wait for Collapse expand animation + body paint before scrolling.
    window.setTimeout(scrollSuggestionsIntoView, 320);
  };

  return (
    <Space
      orientation="vertical"
      size={12}
      style={{
        width: '100%',
        background: WORK_ORDER_UI.pageBg,
        padding: '4px 0 20px',
        boxSizing: 'border-box',
      }}
    >
      <Flex
        gap={12}
        wrap="wrap"
        align="flex-start"
        justify="space-between"
        style={{ rowGap: 10 }}
      >
        <div style={{ minWidth: 0, flex: '1 1 220px' }}>
          <Typography.Title
            level={3}
            style={{
              margin: '0 0 2px',
              color: WORK_ORDER_UI.sidebarNavy,
              fontSize: 22,
              lineHeight: 1.25,
            }}
          >
            {t('workOrders.kanban.title')}
          </Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            {t('workOrders.kanban.description')}
          </Typography.Text>
        </div>

        <WorkOrderKanbanFilters
          siteFilter={page.siteFilter}
          onSiteFilterChange={page.setSiteFilter}
          siteOptions={page.siteFilterOptions}
          technicianFilter={page.technicianFilter}
          onTechnicianFilterChange={page.setTechnicianFilter}
          technicianOptions={page.technicianOptions}
          planFilter={page.planFilter}
          onPlanFilterChange={page.setPlanFilter}
          planOptions={page.planFilterOptions}
          kanbanSort={page.kanbanSort}
          onKanbanSortChange={page.setKanbanSort}
          dueRange={page.dueRange}
          onDueRangeChange={page.setDueRange}
          followUpOnly={page.followUpOnly}
          onFollowUpOnlyChange={page.setFollowUpOnly}
        />
      </Flex>

      <WorkOrderOptimizerTeaser
        organizationId={page.organizationId}
        siteFilter={page.siteFilter}
        onOpenSuggestions={openOptimizerAccordion}
      />

      <FollowUpPanel
        tickets={page.followUpTickets}
        onDismiss={() => page.setFollowUpTickets([])}
        onMove={(ticket) =>
          page.openMoveForTicket(ticket, ticket.sourceWorkOrderId)
        }
        onCreateNew={(ticket) =>
          page.openMoveForTicket(ticket, ticket.sourceWorkOrderId, {
            createNewWorkOrder: true,
          })
        }
      />

      {page.isError ? (
        <Alert
          type="error"
          title={t('workOrders.kanban.messages.loadFailed')}
        />
      ) : (
        <WorkOrderKanbanBoard
          columns={page.columns}
          columnsMap={page.columnsMap}
          canManage={page.canManage}
          organizationId={page.organizationId}
          selectedWorkOrderId={page.selectedWorkOrder?.id ?? null}
          onSelectWorkOrder={page.setSelectedWorkOrderId}
          onOpenMoveTicket={page.openMoveTicketFromBoard}
          onDragEnd={page.onDragEnd}
        />
      )}

      <WorkOrderOptimizerAccordion
        organizationId={page.organizationId}
        siteFilter={page.siteFilter}
        workOrders={page.workOrders}
        siteNameById={page.siteNameById}
        open={optimizerOpen}
        onOpenChange={setOptimizerOpen}
        onSelectWorkOrder={page.setSelectedWorkOrderId}
      />

      <WorkOrderDetailDrawer
        open={page.selectedWorkOrderId != null}
        workOrderId={page.selectedWorkOrderId}
        workOrderCode={page.selectedWorkOrder?.code}
        onClose={() => page.setSelectedWorkOrderId(null)}
        canManage={page.canManage}
        onMoveTicket={(ticketId) => {
          const ticket = page.selectedWorkOrder?.tickets.find(
            (item) => item.id === ticketId,
          );
          if (ticket && page.selectedWorkOrder) {
            page.openMoveForTicket(ticket, page.selectedWorkOrder.id);
          }
        }}
        onCreateNewWorkOrder={(ticketId) => {
          const ticket = page.selectedWorkOrder?.tickets.find(
            (item) => item.id === ticketId,
          );
          if (ticket && page.selectedWorkOrder) {
            page.openMoveForTicket(ticket, page.selectedWorkOrder.id, {
              createNewWorkOrder: true,
            });
          }
        }}
      />

      <WorkOrderAssignDialog
        open={page.pendingAssign != null}
        workOrderCode={page.pendingAssign?.workOrderCode ?? ''}
        technicianOptions={page.technicianOptions}
        loading={page.isAssigning}
        onCancel={() => page.setPendingAssign(null)}
        onConfirm={page.confirmAssign}
      />

      <WorkOrderHoldDialog
        open={page.pendingHold != null}
        workOrderCode={page.pendingHold?.workOrderCode ?? ''}
        loading={page.isHolding}
        onCancel={() => page.setPendingHold(null)}
        onConfirm={page.confirmHold}
      />

      <WorkOrderCompleteModal
        open={page.pendingComplete != null}
        workOrderCode={page.pendingComplete?.workOrderCode ?? ''}
        tickets={page.pendingComplete?.tickets ?? []}
        loading={page.isCompleting}
        onCancel={() => page.setPendingComplete(null)}
        onConfirm={page.confirmComplete}
      />

      <MoveTicketDialog
        open={page.pendingMove != null}
        ticketCode={page.pendingMove?.ticketCode ?? ''}
        sourceWorkOrderCode={page.pendingMove?.sourceWorkOrderCode}
        sourceSiteId={page.pendingMove?.sourceSiteId}
        sourceSiteName={page.pendingMove?.sourceSiteName}
        targetWorkOrderCode={page.pendingMove?.targetWorkOrderCode}
        targetSiteId={page.pendingMove?.targetSiteId}
        targetSiteName={page.pendingMove?.targetSiteName}
        preselectedTargetWorkOrderId={page.pendingMove?.targetWorkOrderId}
        createNewWorkOrder={page.pendingMove?.createNewWorkOrder === true}
        targetOptions={page.moveTargetOptions}
        loading={page.isMoving}
        onCancel={() => page.setPendingMove(null)}
        onConfirm={page.confirmMoveFromPicker}
      />
    </Space>
  );
}

export function WorkOrderKanbanPageContainer() {
  return (
    <AuthGuard requiredPermissions={[PermissionKey.WORK_ORDER_READ]}>
      <WorkOrderKanbanPageView />
    </AuthGuard>
  );
}
